/**
 * Last-known Oroboros session for the current tab.
 * Identity only — never persist the access JWT (httpOnly cookie + in-memory).
 */

import type { OroborosSessionUser } from './api/session';

export const OROBOROS_SESSION_SNAPSHOT_KEY = 'oro_session_snapshot_v1';

export type OroborosSessionSnapshot = {
  user: OroborosSessionUser;
  requiresHlOnboarding: boolean;
  hasHyperliquidKey: boolean;
  hyperliquidKeyActive: boolean;
  hyperliquidAccountMismatch: boolean;
  hlExecutionReady: boolean;
};

let memorySnapshot: OroborosSessionSnapshot | null = null;

function asUser(raw: unknown): OroborosSessionUser | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const userId = String(row.userId || '').trim();
  if (!userId) return null;
  const username = String(row.username || '').trim() || undefined;
  const wallet = String(row.walletAddress || '').trim().toLowerCase();
  const authMethod =
    row.authMethod === 'wallet' ||
    row.authMethod === 'password' ||
    row.authMethod === 'telegram' ||
    row.authMethod === 'artifice'
      ? row.authMethod
      : undefined;
  return {
    userId,
    telegramId: String(row.telegramId || '').trim() || undefined,
    username,
    hasPasswordLogin: !!row.hasPasswordLogin || !!username,
    authMethod,
    walletAddress: /^0x[a-f0-9]{40}$/.test(wallet) ? wallet : undefined,
    isSynthetic: !!row.isSynthetic,
    isWalletOnly: !!row.isWalletOnly,
  };
}

function asSnapshot(raw: unknown): OroborosSessionSnapshot | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  const user = asUser(row.user);
  if (!user) return null;
  return {
    user,
    requiresHlOnboarding: !!row.requiresHlOnboarding,
    hasHyperliquidKey: !!row.hasHyperliquidKey,
    hyperliquidKeyActive: row.hyperliquidKeyActive !== false,
    hyperliquidAccountMismatch: !!row.hyperliquidAccountMismatch,
    hlExecutionReady: !!row.hlExecutionReady,
  };
}

export function oroborosSessionUsersEqual(
  a: OroborosSessionUser | null | undefined,
  b: OroborosSessionUser | null | undefined,
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.userId === b.userId &&
    (a.telegramId || '') === (b.telegramId || '') &&
    (a.username || '') === (b.username || '') &&
    !!a.hasPasswordLogin === !!b.hasPasswordLogin &&
    (a.authMethod || '') === (b.authMethod || '') &&
    (a.walletAddress || '') === (b.walletAddress || '') &&
    !!a.isWalletOnly === !!b.isWalletOnly &&
    !!a.isSynthetic === !!b.isSynthetic
  );
}

export function oroborosSessionSnapshotsEqual(
  a: OroborosSessionSnapshot | null | undefined,
  b: OroborosSessionSnapshot | null | undefined,
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    oroborosSessionUsersEqual(a.user, b.user) &&
    a.requiresHlOnboarding === b.requiresHlOnboarding &&
    a.hasHyperliquidKey === b.hasHyperliquidKey &&
    a.hyperliquidKeyActive === b.hyperliquidKeyActive &&
    a.hyperliquidAccountMismatch === b.hyperliquidAccountMismatch &&
    a.hlExecutionReady === b.hlExecutionReady
  );
}

export function readOroborosSessionSnapshot(): OroborosSessionSnapshot | null {
  if (memorySnapshot) return memorySnapshot;
  try {
    const raw = sessionStorage.getItem(OROBOROS_SESSION_SNAPSHOT_KEY);
    if (!raw) return null;
    const parsed = asSnapshot(JSON.parse(raw));
    memorySnapshot = parsed;
    return parsed;
  } catch {
    return null;
  }
}

export function writeOroborosSessionSnapshot(next: OroborosSessionSnapshot | null): void {
  if (!next?.user?.userId) {
    clearOroborosSessionSnapshot();
    return;
  }
  if (oroborosSessionSnapshotsEqual(memorySnapshot, next)) return;
  memorySnapshot = next;
  try {
    sessionStorage.setItem(OROBOROS_SESSION_SNAPSHOT_KEY, JSON.stringify(next));
  } catch {
    /* ignore quota / private mode */
  }
}

export function clearOroborosSessionSnapshot(): void {
  memorySnapshot = null;
  try {
    sessionStorage.removeItem(OROBOROS_SESSION_SNAPSHOT_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Keep the last painted account unless the server (or login) proved the session is gone.
 * Network / 5xx / overlap-refresh failures must not flip the terminal into demo.
 */
export function shouldClearOroborosSessionOnRefreshFailure(opts: {
  afterLogin?: boolean;
  hasLiveToken: boolean;
  hasCachedUser: boolean;
  httpStatus?: number | null;
}): boolean {
  if (opts.hasLiveToken) return false;
  if (opts.afterLogin) return false;
  if (!opts.hasCachedUser) return true;
  const status = opts.httpStatus;
  if (status == null || status === 0 || status >= 500) return false;
  return status === 401 || status === 403 || status === 200 || status === 204;
}

export function resetOroborosSessionCacheForTests(): void {
  memorySnapshot = null;
}
